import { useEffect } from "react";
import { createForm } from "../../utils/formUtils";
import useForm from "../../hooks/common/useForm";
import useSet from "../../hooks/common/useSet";
import {
  formHooks,
  formSubmitHooks,
} from "../../hooks/handlers/useFormHandler";
import { beautify, getValidators } from "../../utils/helpers";
import styled from "styled-components";
import { Button, Modal } from "antd";
import { Create } from "@mui/icons-material";
import useGet from "../../hooks/common/useGet";
import { sourceApis } from "../../hooks/handlers/useApiHandler";
import Loader from "../common/Loader";
import { TFormLayoutHandler } from "../../types/common";
import { useToasts } from "../../hooks/common/useToasts";
import { useSearchParams } from "react-router-dom";
import { SaveOutline } from "@raresail/react-ionicons";
import { primaryColor } from "../styled/Theme";

const FormLayoutHandler = ({
  id,
  source_id: source,
  customTitle,
  draft_id,
  form_params,
}: TFormLayoutHandler) => {
  const {
    form,
    editedForm,
    updateFormValues,
    updateFormValue,
    onChange,
    validateForm,
  } = useForm();

  const {
    error,
    setError,
    uploadData,
    submitting,
    success,
    data: submitResponse,
  } = useSet();

  const {
    error: draftError,
    uploadData: draftUploadData,
    submitting: draftSubmitting,
    success: draftSuccess,
    data: draftSubmitResponse,
  } = useSet();

  const {
    uploadData: deleteDraft,
    success: deleteDraftSuccess,
    error: deleteDraftError,
  } = useSet();

  const { data, loadData, loading } = useGet();
  const [sParams] = useSearchParams();
  const searchParams = Object.fromEntries(sParams.entries()) || {};

  // destructure form hooks using source id
  const {
    sections,
    title,
    redirect,
    options,
    defaultValues,
    callback,
    onSubmit: handleSubmit,
    submitDisabled,
    params,
    draftButtonOnClick,
    draftRedirect,
    draftsApi,
    preview,
  } = source && formHooks[source]
    ? formHooks[source]({
        form,
        updateFormValue,
        onChange,
        updateFormValues,
        setError,
        id,
        searchParams,
        form_params,
      })
    : [];

  // set form default values
  useEffect(() => {
    if (defaultValues) {
      updateFormValues(defaultValues);
    }
  }, []);

  // handle notification
  useToasts({
    success,
    error,
    redirect,
    callback,
    id,
    submitResponse,
    source,
    submitting,
  });

  // handle secondary notification
  useToasts({
    success: draftSuccess,
    error: draftError,
    redirect: draftRedirect,
    id,
    submitResponse: draftSubmitResponse,
    submitting: draftSubmitting,
    successMessage: "Draft Saved Successfully",
  });
  // handle delete draft notification
  useToasts({
    success: deleteDraftSuccess,
    error: deleteDraftError,
    successMessage: "Draft Deleted Successfully",
  });

  // get record data from api
  useEffect(() => {
    if (id && source && sourceApis[source]) {
      loadData({ id, api: sourceApis[source], params });
    }
  }, [id]);

  // get draft data from api
  useEffect(() => {
    if (draft_id && draftsApi) {
      loadData({ id: draft_id, api: draftsApi, params });
    }
  }, [draft_id]);

  useEffect(() => {
    // if success is true, then delete draft
    if (success && draft_id && draftsApi) {
      deleteDraft({ api: draftsApi, id: draft_id, method: "DELETE" });
    }
  }, [success]);

  // update form data
  useEffect(() => {
    if (data) {
      updateFormValues(data);
    }
  }, [data]);

  // submit data
  const onSubmit = (payload: any) => {
    if (source) {
      // construct upload data
      delete payload.id;
      delete payload.created_by;
      delete payload.application_number;
      delete payload.created_at;
      delete payload.updated_at;

      let upload_data: any;

      // submit method from a form hook
      if (handleSubmit) {
        upload_data = handleSubmit(payload, id);
      } else if (formSubmitHooks[source]) {
        // submit method from form handler
        upload_data = formSubmitHooks[source](payload, id, form_params);
      }

      if (!upload_data) return;

      // let the user review what is about to be submitted
      if (preview) {
        Modal.confirm({
          title: `Preview ${title || beautify(source)}`,
          content: preview(payload),
          okText: "Submit",
          cancelText: "Back to Form",
          width: 560,
          onOk: () => uploadData(upload_data),
        });
        return;
      }

      // handle submission
      return uploadData(upload_data);
    }
  };

  const onDraftButtonClick = () => {
    if (draftButtonOnClick) {
      const payload = draft_id ? editedForm : form;
      const upload_data = draftButtonOnClick(payload, draft_id);
      if (upload_data) return draftUploadData(upload_data);
    }
  };

  const submitForm = (e: any) => {
    e.preventDefault();
    const payload = id ? editedForm : form;
    setError({});
    if (validateForm(getValidators(sections), setError)) onSubmit(payload);
  };

  if (loading) return <Loader />;

  return (
    <form onSubmit={submitForm}>
      <FormWrapper>
        <FormTitleBar>
          <Create fontSize="small" style={{ color: primaryColor }} />
          <span className="form-title">
            {customTitle || title || beautify(source)} Form
          </span>
        </FormTitleBar>

        {/* Form Creation */}
        <div className="form-body">
          {createForm(
            sections,
            form,
            updateFormValues,
            updateFormValue,
            onChange,
            error,
            options,
            setError,
          )}
        </div>

        {/* Submit Button */}
        <FormActions>
          {draftButtonOnClick && !id && (
            <Button
              style={{ minWidth: 110, height: 38 }}
              onClick={onDraftButtonClick}
              loading={draftSubmitting}
            >
              <SaveOutline />
              Save
            </Button>
          )}
          <Button
            type="primary"
            htmlType="submit"
            disabled={
              submitDisabled || (id && Object.keys(editedForm).length <= 0)
            }
            loading={submitting}
            style={{ minWidth: 110, height: 38 }}
          >
            {id ? "Update" : "Submit"}
          </Button>
        </FormActions>
      </FormWrapper>
    </form>
  );
};

export default FormLayoutHandler;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 20px 24px;

  .form-body {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
`;

const FormTitleBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.03),
    0 1px 2px rgba(0, 0, 0, 0.04);

  .form-title {
    font-size: 0.9375rem;
    font-weight: 700;
    color: #111827;
    text-transform: capitalize;
    letter-spacing: -0.01em;
  }
`;

const FormActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 4px;
`;
